import { useLocation } from "react-router-dom";
import { languages } from "@/i18n";
import { LOCALIZED_PATHS } from "@/lib/localizedPaths";

// Per-locale SEO pages are served at STATIC prefixes (/en/models, /es/kits, …),
// NOT a dynamic `/:lng/…` segment — a dynamic first segment shadowed the app's
// /dashboard/* routes (see App.js + memory project_locale_route_shadowing). A
// side effect of that static-prefix migration: `useParams().lng` is ALWAYS
// undefined on these pages, so pages that built links/canonicals from it fell
// back to the un-prefixed French root — meaning a Spanish visitor on /es/models
// got links to /models/:slug and a French canonical, silently dropping the
// locale on the whole international SEO surface.
//
// Derive the prefix from the URL's first path segment instead (the URL is the
// source of truth for which locale page is rendered). French is canonical at
// the un-prefixed root, so it yields "".
const NON_FR_CODES = new Set(
  languages.map((l) => l.code).filter((c) => c !== "fr")
);

export function useLocalePrefix() {
  const seg = useLocation().pathname.split("/")[1];
  return NON_FR_CODES.has(seg) ? `/${seg}` : "";
}

// Returns a builder that keeps an internal link in the visitor's locale, but
// ONLY for paths that actually have a per-locale URL (LOCALIZED_PATHS, the
// single source of truth). Paths without a localized version (/contact,
// /privacy, /terms, /legal, /login, /register, /dashboard…) are returned as-is,
// so we never link to a locale URL that doesn't exist. French → un-prefixed.
export function useLocalizedTo() {
  const prefix = useLocalePrefix();
  return (path) => {
    if (!LOCALIZED_PATHS.has(path)) return path;
    if (path === "/") return prefix || "/";
    return prefix + path;
  };
}
